import { ActivityAlertSettings } from "@core/domain/models/activity-alert-settings.model";
import type { Nullable } from "@core/domain/types/nullable.type";
import type { Undefinable } from "@core/domain/types/undefinable.type";
import { GetMyActivityAlertSettingsUseCase } from "@core/domain/usecases/get-my-activity-alert-settings.usecase";
import { UpdateMyActivityAlertSettingsUseCase } from "@core/domain/usecases/update-my-activity-alert-settings.usecase";
import { LoadLayoutStore } from "@core/presentacion/component/feedback/load-layout/load-layout.store";
import { ToastManagerStore } from "@core/presentacion/component/feedback/toast-manager/toast-manager.store";
import { BaseViewModel } from "@core/presentacion/view-model/base/base.viewmodel";
import { Employee } from "@entity/domain/models/employee/employee.model";
import { GetAllEmployeesByUseCase } from "@entity/domain/usecases/employee/get-all-employees-by.usecase";
import i18n from "@i18n";
import type {
    KanbanPendingByAssigneeBucket,
    KanbanPendingByTeamBucket,
    KanbanReportActivityItem,
    KanbanStatusSummary,
} from "@project/domain/models/kanban-reports.model";
import {
    CreateProjectActivityTaskBody,
    ProjectActivityTask,
} from "@project/domain/models/project-activity-task.model";
import type { BoardSections } from "@project/domain/models/project-kanban-activity.model";
import { ProjectKanbanBlock } from "@project/domain/models/project-kanban-block.model";
import {
    Activity,
    ActivityPriorityEnum,
    ActivityStatusEnum,
    CreateIndicatorComment,
    Indicator,
    IndicatorCommentTypeEnum,
    IndicatorGenderBreakdown,
} from "@project/domain/models/technical-proposals.model";
import { GetKanbanReportsUseCase } from "@project/domain/usecases/kanban/get-kanban-reports.usecase";
import { CreateActivityTaskUseCase } from "@project/domain/usecases/project-activity/create-activity-task.usecase";
import { DeleteActivityTaskUseCase } from "@project/domain/usecases/project-activity/delete-activity-task.usecase";
import { EditKanbanActivityUseCase } from "@project/domain/usecases/project-activity/edit-kanban-activity.usecase";
import { GetAllActivitiesByUseCase } from "@project/domain/usecases/project-activity/get-all-activities-by.usecase";
import { UpdateActivityTaskUseCase } from "@project/domain/usecases/project-activity/update-activity-task.usecase";
import { CreateProjectKanbanBlockUseCase } from "@project/domain/usecases/project-kanban-block/create-project-kanban-block.usecase";
import { DeleteProjectKanbanBlockUseCase } from "@project/domain/usecases/project-kanban-block/delete-project-kanban-block.usecase";
import { EditProjectKanbanBlockUseCase } from "@project/domain/usecases/project-kanban-block/edit-project-kanban-block.usecase";
import { GetAllProjectKanbanBlocksByUseCase } from "@project/domain/usecases/project-kanban-block/get-all-project-kanban-blocks-by.usecase";
import { ReorderProjectKanbanBlocksUseCase } from "@project/domain/usecases/project-kanban-block/reorder-project-kanban-blocks.usecase";
import { CreateIndicatorCommentUseCase } from "@project/domain/usecases/technical-proposal/indicator/create-indicator-comment.usecase";
import { EditIndicatorUseCase } from "@project/domain/usecases/technical-proposal/indicator/edit-indicator.usecase";
import { activityMatchesResponsibleFilter } from "@project/domain/utils/activity-responsible.util";
import { KanbanActivityFormValuesValidated } from "@project/presentation/components/project-form/kanban/form/kanban-activity.form";
import { Map } from "immutable";
import { inject, injectable } from "inversify";
import { DateTime } from "luxon";
import { action, makeObservable, observable, runInAction } from "mobx";

type IndicatorProgressSummary = {
    indicatorId: number;
    completedCount: number;
    totalCount: number;
};

type ParsedNumericIndicatorValue = {
    value: number;
    decimals: number;
    usesComma: boolean;
    match: string;
};

const GENDER_BREAKDOWN_KEYS = [
    "women",
    "men",
    "nonBinary",
    "unspecified",
] as const;

type GenderBreakdownKey = (typeof GENDER_BREAKDOWN_KEYS)[number];

const parseNumericIndicatorValue = (
    rawValue: string | null | undefined,
): ParsedNumericIndicatorValue | null => {
    if (!rawValue) return null;

    const trimmedValue = rawValue.trim();
    if (!trimmedValue.length) return null;

    const match = trimmedValue.match(/-?\d+(?:[.,]\d+)?/);
    if (!match) return null;

    const numericFragment = match[0];
    const normalizedValue = Number(numericFragment.replace(",", "."));

    if (Number.isNaN(normalizedValue)) return null;

    const decimalFragment = numericFragment.split(/[.,]/)[1] ?? "";

    return {
        value: normalizedValue,
        decimals: decimalFragment.length,
        usesComma: numericFragment.includes(","),
        match: numericFragment,
    };
};

const formatNumericIndicatorValue = (
    template: string,
    numericValue: number,
): string => {
    const parsedTemplate = parseNumericIndicatorValue(template);

    if (!parsedTemplate) return template;

    const formattedValue =
        parsedTemplate.decimals > 0
            ? numericValue.toFixed(parsedTemplate.decimals)
            : `${Math.round(numericValue)}`;

    return template.replace(
        parsedTemplate.match,
        parsedTemplate.usesComma
            ? formattedValue.replace(".", ",")
            : formattedValue,
    );
};

const appendSuggestion = (
    currentValue: string | null | undefined,
    suggestion: string,
): string => {
    const trimmedCurrentValue = currentValue?.trim() ?? "";

    if (!trimmedCurrentValue.length) return suggestion;
    if (trimmedCurrentValue.includes(suggestion)) return trimmedCurrentValue;

    return `${trimmedCurrentValue} | ${suggestion}`;
};

const getBreakdownNumericSum = (breakdown: IndicatorGenderBreakdown): number =>
    GENDER_BREAKDOWN_KEYS.reduce(
        (sum, key) =>
            sum +
            (parseNumericIndicatorValue(breakdown[key] ?? undefined)?.value ??
                0),
        0,
    );

@injectable()
export class KanbanTabViewModel extends BaseViewModel {
    projectId: Undefinable<number>;

    focusedActivityId: Undefinable<number>;

    @observable
    initialLoading: boolean = true;

    @observable
    editActivityModalOpen: boolean = false;

    @observable
    activityToEdit: Undefinable<Activity> = undefined;

    @observable
    employees: Employee[] = [];

    @observable
    kanbanBlocks: ProjectKanbanBlock[] = [];

    @observable
    selectedBlockId: Nullable<number> = null;

    @observable
    filterResponsibleEmployeeIds: number[] = [];

    @observable
    filterPhase: Nullable<string> = null;

    @observable
    filterPriority: Nullable<ActivityPriorityEnum> = null;

    @observable
    filterStatus: Nullable<ActivityStatusEnum> = null;

    @observable
    filterDueDateMode: "none" | "before" | "between" | "overdue" = "none";

    @observable
    filterDueDateFrom: Nullable<DateTime> = null;

    @observable
    filterDueDateTo: Nullable<DateTime> = null;

    private ensureSelectedBlockIdIsValid(): void {
        if (this.selectedBlockId === null) return;

        const exists = this.kanbanBlocks.some(
            (block) => block.id === this.selectedBlockId,
        );
        if (!exists) this.selectedBlockId = null;
    }

    @observable
    groupBy: "none" | "block" | "phase" = "block";

    @observable
    boardSections: BoardSections = Map([
        [ActivityStatusEnum.toDo, []],
        [ActivityStatusEnum.inProgress, []],
        [ActivityStatusEnum.blocked, []],
        [ActivityStatusEnum.done, []],
    ]);

    @observable
    alertSettingsModalOpen: boolean = false;

    @observable
    alertSettingsLoading: boolean = false;

    @observable
    alertSettingsSaving: boolean = false;

    @observable
    alertSettings: ActivityAlertSettings = ActivityAlertSettings.defaults();

    @observable
    manageBlocksModalOpen: boolean = false;

    @observable
    manageBlocksLoading: boolean = false;

    @observable
    manageBlocksSaving: boolean = false;

    @observable
    reportsModalOpen: boolean = false;

    @observable
    reportsLoading: boolean = false;

    @observable
    reportsStatusSummary: KanbanStatusSummary | null = null;

    @observable
    reportsPending: KanbanReportActivityItem[] = [];

    @observable
    reportsPendingByAssignee: KanbanPendingByAssigneeBucket[] = [];

    @observable
    reportsPendingByTeam: KanbanPendingByTeamBucket[] = [];

    @observable
    taskIndicatorUpdateProposalOpen: boolean = false;

    @observable
    taskIndicatorUpdateSaving: boolean = false;

    @observable
    completedTaskToReview: Nullable<ProjectActivityTask> = null;

    @observable
    indicatorsToReview: Indicator[] = [];

    constructor(
        @inject(GetAllActivitiesByUseCase)
        private readonly getAllActivitiesByUseCase: GetAllActivitiesByUseCase,
        @inject(EditKanbanActivityUseCase)
        private readonly editKanbanActivityUseCase: EditKanbanActivityUseCase,
        @inject(GetAllEmployeesByUseCase)
        private readonly getAllEmployeesByUseCase: GetAllEmployeesByUseCase,
        @inject(GetAllProjectKanbanBlocksByUseCase)
        private readonly getAllProjectKanbanBlocksByUseCase: GetAllProjectKanbanBlocksByUseCase,
        @inject(CreateProjectKanbanBlockUseCase)
        private readonly createProjectKanbanBlockUseCase: CreateProjectKanbanBlockUseCase,
        @inject(EditProjectKanbanBlockUseCase)
        private readonly editProjectKanbanBlockUseCase: EditProjectKanbanBlockUseCase,
        @inject(DeleteProjectKanbanBlockUseCase)
        private readonly deleteProjectKanbanBlockUseCase: DeleteProjectKanbanBlockUseCase,
        @inject(ReorderProjectKanbanBlocksUseCase)
        private readonly reorderProjectKanbanBlocksUseCase: ReorderProjectKanbanBlocksUseCase,
        @inject(CreateActivityTaskUseCase)
        private readonly createActivityTaskUseCase: CreateActivityTaskUseCase,
        @inject(DeleteActivityTaskUseCase)
        private readonly deleteActivityTaskUseCase: DeleteActivityTaskUseCase,
        @inject(UpdateActivityTaskUseCase)
        private readonly updateActivityTaskUseCase: UpdateActivityTaskUseCase,
        @inject(EditIndicatorUseCase)
        private readonly editIndicatorUseCase: EditIndicatorUseCase,
        @inject(CreateIndicatorCommentUseCase)
        private readonly createIndicatorCommentUseCase: CreateIndicatorCommentUseCase,
        @inject(GetMyActivityAlertSettingsUseCase)
        private readonly getMyActivityAlertSettingsUseCase: GetMyActivityAlertSettingsUseCase,
        @inject(UpdateMyActivityAlertSettingsUseCase)
        private readonly updateMyActivityAlertSettingsUseCase: UpdateMyActivityAlertSettingsUseCase,
        @inject(GetKanbanReportsUseCase)
        private readonly getKanbanReportsUseCase: GetKanbanReportsUseCase,
    ) {
        super();
        makeObservable(this);
    }

    @action
    async openReportsModal(): Promise<void> {
        this.reportsModalOpen = true;
        await this.loadReports();
    }

    @action
    closeReportsModal(): void {
        this.reportsModalOpen = false;
    }

    @action
    async loadReports(): Promise<void> {
        if (!this.projectId) return;

        this.reportsLoading = true;

        const reports = await this.getKanbanReportsUseCase.execute(
            this.projectId,
        );

        runInAction(() => {
            this.reportsStatusSummary = reports.statusSummary;
            this.reportsPending = reports.pending;
            this.reportsPendingByAssignee = reports.pendingByAssignee;
            this.reportsPendingByTeam = reports.pendingByTeam;
            this.reportsLoading = false;
        });
    }

    override async didMount(): Promise<void> {
        await this.initViewData();
    }

    async initViewData(): Promise<void> {
        await Promise.all([
            this.getAllActivities(),
            this.getAllEmployees(),
            this.getAllKanbanBlocks(),
        ]);

        runInAction(() => {
            this.openFocusedActivity();
            this.initialLoading = false;
        });
    }

    private openFocusedActivity(): void {
        if (this.focusedActivityId === undefined) return;

        const focusedActivity = this.boardSections
            .toArray()
            .reduce(
                (activities: Activity[], [, sectionActivities]) =>
                    activities.concat(sectionActivities),
                [],
            )
            .find((activity) => activity.id === this.focusedActivityId);

        if (!focusedActivity) return;

        this.setActivityToEdit(focusedActivity);
        this.editActivityModalOpen = true;
    }

    private getBlockPositionRank(
        blockId: Nullable<number> | undefined,
    ): number {
        if (!blockId) return Number.MAX_SAFE_INTEGER;

        const block = this.kanbanBlocks.find((b) => b.id === blockId);
        if (!block) return Number.MAX_SAFE_INTEGER - 1;

        return block.position;
    }

    private sortActivitiesForView(activities: Activity[]): Activity[] {
        if (this.groupBy === "none") return activities;

        if (this.groupBy === "block") {
            return [...activities].sort(
                (a, b) =>
                    this.getBlockPositionRank(a.blockId) -
                    this.getBlockPositionRank(b.blockId),
            );
        }

        if (this.groupBy === "phase") {
            const phaseRank = (phase: string): number => {
                if (phase === "planning") return 0;
                if (phase === "execution") return 1;
                if (phase === "evaluation") return 2;
                return 3;
            };

            const phaseByBlockId = new globalThis.Map<number, string>(
                this.kanbanBlocks.map((block) => [block.id, block.phase]),
            );

            return [...activities].sort((a, b) => {
                const aPhase = a.blockId
                    ? phaseByBlockId.get(a.blockId) ?? ""
                    : "";
                const bPhase = b.blockId
                    ? phaseByBlockId.get(b.blockId) ?? ""
                    : "";

                const byPhase = phaseRank(aPhase) - phaseRank(bPhase);
                if (byPhase !== 0) return byPhase;

                return (
                    this.getBlockPositionRank(a.blockId) -
                    this.getBlockPositionRank(b.blockId)
                );
            });
        }

        return activities;
    }

    @action
    private sortBoardSections(): void {
        this.boardSections = this.boardSections.update((boardSections) => {
            return boardSections
                .set(
                    ActivityStatusEnum.toDo,
                    this.sortActivitiesForView(
                        boardSections.get(ActivityStatusEnum.toDo) ?? [],
                    ),
                )
                .set(
                    ActivityStatusEnum.inProgress,
                    this.sortActivitiesForView(
                        boardSections.get(ActivityStatusEnum.inProgress) ?? [],
                    ),
                )
                .set(
                    ActivityStatusEnum.blocked,
                    this.sortActivitiesForView(
                        boardSections.get(ActivityStatusEnum.blocked) ?? [],
                    ),
                )
                .set(
                    ActivityStatusEnum.done,
                    this.sortActivitiesForView(
                        boardSections.get(ActivityStatusEnum.done) ?? [],
                    ),
                );
        });
    }

    @action
    async getAllActivities(): Promise<void> {
        if (this.projectId) {
            const activities = await this.getAllActivitiesByUseCase.execute({
                project: this.projectId,
            });

            runInAction(() => {
                this.boardSections = this.boardSections.update(
                    (boardSections) => {
                        boardSections = boardSections.set(
                            ActivityStatusEnum.toDo,
                            [],
                        );
                        boardSections = boardSections.set(
                            ActivityStatusEnum.inProgress,
                            [],
                        );
                        boardSections = boardSections.set(
                            ActivityStatusEnum.blocked,
                            [],
                        );
                        boardSections = boardSections.set(
                            ActivityStatusEnum.done,
                            [],
                        );

                        activities.forEach((activity) => {
                            const currentActivities =
                                boardSections.get(activity.status) ?? [];

                            boardSections = boardSections.set(activity.status, [
                                ...currentActivities,
                                activity,
                            ]);
                        });

                        return boardSections;
                    },
                );

                this.sortBoardSections();
            });
        }
    }

    async getAllEmployees(): Promise<void> {
        const employees = await this.getAllEmployeesByUseCase.execute();

        runInAction(() => {
            this.employees = employees;
        });
    }

    async getAllKanbanBlocks(): Promise<void> {
        if (!this.projectId) return;

        const blocks = await this.getAllProjectKanbanBlocksByUseCase.execute({
            project: this.projectId,
        });

        runInAction(() => {
            this.kanbanBlocks = blocks;

            this.ensureSelectedBlockIdIsValid();

            this.sortBoardSections();
        });
    }

    @action
    setSelectedBlockId(blockId: Nullable<number>): void {
        this.selectedBlockId = blockId;
    }

    @action
    setFilterResponsibleEmployeeIds(employeeIds: number[]): void {
        this.filterResponsibleEmployeeIds = employeeIds;
    }

    @action
    setFilterPhase(phase: Nullable<string>): void {
        this.filterPhase = phase;
    }

    @action
    setFilterPriority(priority: Nullable<ActivityPriorityEnum>): void {
        this.filterPriority = priority;
    }

    @action
    setFilterStatus(status: Nullable<ActivityStatusEnum>): void {
        this.filterStatus = status;
    }

    @action
    clearFilters(): void {
        this.selectedBlockId = null;
        this.filterResponsibleEmployeeIds = [];
        this.filterPhase = null;
        this.filterPriority = null;
        this.filterStatus = null;
        this.filterDueDateMode = "none";
        this.filterDueDateFrom = null;
        this.filterDueDateTo = null;
    }

    @action
    setFilterDueDateMode(
        mode: "none" | "before" | "between" | "overdue",
    ): void {
        this.filterDueDateMode = mode;

        if (mode === "none" || mode === "overdue") {
            this.filterDueDateFrom = null;
            this.filterDueDateTo = null;
            return;
        }

        if (mode === "before") {
            this.filterDueDateTo = null;
            return;
        }
    }

    @action
    setFilterDueDateFrom(date: Nullable<DateTime>): void {
        this.filterDueDateFrom = date;
    }

    @action
    setFilterDueDateTo(date: Nullable<DateTime>): void {
        this.filterDueDateTo = date;
    }

    private getActivityDueDate(activity: Activity): Nullable<DateTime> {
        return activity.estimatedExecutionEndDate ?? activity.deadline ?? null;
    }

    private matchesActivityFilters(activity: Activity): boolean {
        if (
            this.selectedBlockId !== null &&
            activity.blockId !== this.selectedBlockId
        ) {
            return false;
        }

        if (
            this.filterStatus !== null &&
            activity.status !== this.filterStatus
        ) {
            return false;
        }

        if (
            !activityMatchesResponsibleFilter(
                activity,
                this.filterResponsibleEmployeeIds,
            )
        ) {
            return false;
        }

        if (
            this.filterPriority !== null &&
            activity.priority !== this.filterPriority
        ) {
            return false;
        }

        if (this.filterPhase !== null) {
            const phaseByBlockId = new globalThis.Map<number, string>(
                this.kanbanBlocks.map((block) => [block.id, block.phase]),
            );

            const activityPhase = activity.blockId
                ? phaseByBlockId.get(activity.blockId) ?? null
                : null;

            if (this.filterPhase === "__none__") {
                if (activityPhase) return false;
            } else {
                if (activityPhase !== this.filterPhase) return false;
            }
        }

        if (this.filterDueDateMode !== "none") {
            const dueDate = this.getActivityDueDate(activity);
            if (!dueDate) return false;

            const due = dueDate.startOf("day");
            const today = DateTime.now().startOf("day");

            if (this.filterDueDateMode === "overdue") {
                if (activity.status === ActivityStatusEnum.done) return false;
                if (due.toMillis() >= today.toMillis()) return false;
            }

            if (this.filterDueDateMode === "before") {
                const to = (this.filterDueDateFrom ?? null)?.startOf("day");
                if (!to) return false;
                if (due.toMillis() > to.toMillis()) return false;
            }

            if (this.filterDueDateMode === "between") {
                const from = (this.filterDueDateFrom ?? null)?.startOf("day");
                const to = (this.filterDueDateTo ?? null)?.startOf("day");

                if (from && due.toMillis() < from.toMillis()) return false;
                if (to && due.toMillis() > to.toMillis()) return false;

                if (!from && !to) return false;
            }
        }

        return true;
    }

    getVisibleBoardSections(): BoardSections {
        return this.boardSections.map((activities) =>
            activities.filter((activity) =>
                this.matchesActivityFilters(activity),
            ),
        ) as BoardSections;
    }

    private resolveFullIndexInColumn(
        status: ActivityStatusEnum,
        visibleIndex: number,
    ): number {
        const activities = this.boardSections.get(status) ?? [];

        let matchIndex = -1;

        for (let fullIndex = 0; fullIndex < activities.length; fullIndex += 1) {
            if (!this.matchesActivityFilters(activities[fullIndex])) continue;

            matchIndex += 1;
            if (matchIndex === visibleIndex) return fullIndex;
        }

        return -1;
    }

    private resolveFullInsertIndexInColumn(
        status: ActivityStatusEnum,
        visibleIndex: number,
    ): number {
        const activities = this.boardSections.get(status) ?? [];

        let matchIndex = -1;
        let lastMatchFullIndex = -1;

        for (let fullIndex = 0; fullIndex < activities.length; fullIndex += 1) {
            if (!this.matchesActivityFilters(activities[fullIndex])) continue;

            lastMatchFullIndex = fullIndex;
            matchIndex += 1;
            if (matchIndex === visibleIndex) return fullIndex;
        }

        if (lastMatchFullIndex !== -1) return lastMatchFullIndex + 1;
        return activities.length;
    }

    @action
    setManageBlocksModalOpen(open: boolean): void {
        this.manageBlocksModalOpen = open;
    }

    async openManageBlocksModal(): Promise<void> {
        this.setManageBlocksModalOpen(true);
        this.setManageBlocksLoading(true);
        await this.getAllKanbanBlocks();
        this.setManageBlocksLoading(false);
    }

    private setManageBlocksLoading(loading: boolean): void {
        runInAction(() => {
            this.manageBlocksLoading = loading;
        });
    }

    private setManageBlocksSaving(saving: boolean): void {
        runInAction(() => {
            this.manageBlocksSaving = saving;
        });
    }

    async createKanbanBlock(body: {
        name: string;
        phase: ProjectKanbanBlock["phase"];
    }): Promise<boolean> {
        if (!this.projectId) return false;

        this.setManageBlocksSaving(true);

        const created = await this.createProjectKanbanBlockUseCase.execute({
            project: this.projectId,
            name: body.name,
            phase: body.phase,
        });

        this.setManageBlocksSaving(false);

        if (!created) return false;

        await this.getAllKanbanBlocks();
        ToastManagerStore.success();
        return true;
    }

    async updateKanbanBlock(
        id: number,
        body: { name: string; phase: ProjectKanbanBlock["phase"] },
    ): Promise<boolean> {
        this.setManageBlocksSaving(true);

        const updated = await this.editProjectKanbanBlockUseCase.execute(id, {
            name: body.name,
            phase: body.phase,
        });

        this.setManageBlocksSaving(false);

        if (!updated) return false;

        await this.getAllKanbanBlocks();
        ToastManagerStore.success();
        return true;
    }

    async deleteKanbanBlock(id: number): Promise<boolean> {
        this.setManageBlocksSaving(true);

        const ok = await this.deleteProjectKanbanBlockUseCase.execute(id);

        this.setManageBlocksSaving(false);

        if (!ok) return false;

        await this.getAllKanbanBlocks();
        ToastManagerStore.success();
        return true;
    }

    async reorderKanbanBlocks(ids: number[]): Promise<boolean> {
        if (!this.projectId) return false;

        this.setManageBlocksSaving(true);

        const blocks = await this.reorderProjectKanbanBlocksUseCase.execute({
            project: this.projectId,
            ids,
        });

        this.setManageBlocksSaving(false);

        if (!blocks) return false;

        runInAction(() => {
            this.kanbanBlocks = blocks;
            this.ensureSelectedBlockIdIsValid();
            this.sortBoardSections();
        });

        ToastManagerStore.success();
        return true;
    }

    @action
    setGroupBy(next: "none" | "block" | "phase"): void {
        this.groupBy = next;
        this.sortBoardSections();
    }

    async updateActivity(
        activity: KanbanActivityFormValuesValidated | Activity,
    ): Promise<boolean> {
        LoadLayoutStore.start();

        let updatedSuccessfully: boolean = false;

        if (activity instanceof Activity) {
            updatedSuccessfully =
                await this.editKanbanActivityUseCase.edit(activity);
        } else if (this.activityToEdit) {
            const estimatedExecutionEndDate =
                activity.estimatedExecutionEndDate ??
                this.activityToEdit.estimatedExecutionEndDate;

            const blockId =
                activity.blockId !== undefined
                    ? activity.blockId
                    : this.activityToEdit.blockId;

            const responsibleEmployeeIds = activity.responsibleEmployeeIds;

            const actToEdit = new Activity(
                this.activityToEdit.id,
                activity.name,
                activity.description,
                activity.needs,
                this.activityToEdit.objective,
                this.activityToEdit.estimatedExecutionStartDate,
                estimatedExecutionEndDate,
                this.activityToEdit.executionStartDate,
                this.activityToEdit.executionEndDate,
                responsibleEmployeeIds,
                activity.dependencies,
                this.activityToEdit.indicators,
                activity.status,
                responsibleEmployeeIds[0] ?? undefined,
                activity.technicalProposalId ?? undefined,
                estimatedExecutionEndDate,
                activity.comment,
                activity.tasks,
                this.activityToEdit.materialIds,
                activity.priority,
                blockId,
                this.activityToEdit.blockData,
            );

            updatedSuccessfully =
                await this.editKanbanActivityUseCase.edit(actToEdit);
        }

        LoadLayoutStore.finish();

        return updatedSuccessfully;
    }

    async applyDependencyAdjustment(
        updates: Array<{ activityId: number; suggestedEndDate: DateTime }>,
    ): Promise<void> {
        if (!updates.length) return;

        LoadLayoutStore.start();

        const allActivities = this.boardSections
            .toArray()
            .reduce(
                (acc: Activity[], [, activities]) => acc.concat(activities),
                [],
            );

        const updateResults = await Promise.all(
            updates.map(async ({ activityId, suggestedEndDate }) => {
                const existing = allActivities.find(
                    (activity) => activity.id === activityId,
                );
                if (!existing) return false;

                const updatedActivity = new Activity(
                    existing.id,
                    existing.name,
                    existing.description,
                    existing.needs,
                    existing.objective,
                    existing.estimatedExecutionStartDate,
                    suggestedEndDate,
                    existing.executionStartDate,
                    existing.executionEndDate,
                    existing.employeeIds,
                    existing.dependencyIds,
                    existing.indicators,
                    existing.status,
                    existing.assignedEmployee,
                    existing.technicalProposalId,
                    suggestedEndDate,
                    existing.comment,
                    existing.tasks,
                    existing.materialIds,
                    existing.priority,
                    existing.blockId,
                    existing.blockData,
                );

                return this.editKanbanActivityUseCase.edit(updatedActivity);
            }),
        );

        if (updateResults.some(Boolean)) {
            ToastManagerStore.success();
        }

        LoadLayoutStore.finish();
    }

    @action
    toggleEditActivityModalOpen(): void {
        this.editActivityModalOpen = !this.editActivityModalOpen;
        if (!this.editActivityModalOpen) this.activityToEdit = undefined;
    }

    @action
    reorderInColumn(
        currentStatus: ActivityStatusEnum,
        currentIndex: number,
        nextIndex: number,
    ): void {
        this.boardSections = this.boardSections.update((boardSections) => {
            const activities = boardSections.get(currentStatus);

            if (!activities) return boardSections;

            const fullCurrentIndex = this.resolveFullIndexInColumn(
                currentStatus,
                currentIndex,
            );
            if (fullCurrentIndex === -1) return boardSections;

            const fullInsertIndex = this.resolveFullInsertIndexInColumn(
                currentStatus,
                nextIndex,
            );

            const [removed] = activities.splice(fullCurrentIndex, 1);

            const adjustedInsertIndex =
                fullCurrentIndex < fullInsertIndex
                    ? Math.max(0, fullInsertIndex - 1)
                    : fullInsertIndex;

            activities.splice(adjustedInsertIndex, 0, removed);

            return boardSections.set(currentStatus, activities);
        });
    }

    async changeStatus(
        currentStatus: ActivityStatusEnum,
        nextStatus: ActivityStatusEnum,
        currentIndex: number,
        nextIndex: number,
    ): Promise<void> {
        let movedActivity: Activity | null = null;

        this.boardSections = this.boardSections.update((boardSections) => {
            const currentActivities = boardSections.get(currentStatus);
            const nextActivities = boardSections.get(nextStatus);

            if (!currentActivities || !nextActivities) return boardSections;

            const fullCurrentIndex = this.resolveFullIndexInColumn(
                currentStatus,
                currentIndex,
            );
            if (fullCurrentIndex === -1) return boardSections;

            const fullInsertIndex = this.resolveFullInsertIndexInColumn(
                nextStatus,
                nextIndex,
            );

            const [removedActivity] = currentActivities.splice(
                fullCurrentIndex,
                1,
            );

            removedActivity.status = nextStatus;

            movedActivity = removedActivity;

            nextActivities.splice(fullInsertIndex, 0, removedActivity);

            return boardSections
                .set(currentStatus, currentActivities)
                .set(nextStatus, nextActivities);
        });

        if (!movedActivity) return;

        await this.updateActivity(movedActivity);
        await this.getAllActivities();
    }

    @action
    setActivityToEdit(activity: Activity): void {
        this.activityToEdit = activity;

        if (activity.status === ActivityStatusEnum.done) return;
        if (!activity.estimatedExecutionEndDate) return;

        const isDelayed =
            activity.estimatedExecutionEndDate.toMillis() <
            DateTime.now().startOf("day").toMillis();

        if (!isDelayed) return;

        const allActivities = this.boardSections
            .toArray()
            .reduce(
                (acc: Activity[], [, activities]) => acc.concat(activities),
                [],
            );

        const dependentActivitiesCount = allActivities.filter((other) =>
            other.dependencyIds.includes(activity.id),
        ).length;

        if (!dependentActivitiesCount) return;

        ToastManagerStore.warning(
            i18n.t("private:project.kanban.dependencyImpactWarning", {
                count: dependentActivitiesCount,
            }),
        );
    }

    async onAddTask(
        activityTask: CreateProjectActivityTaskBody,
    ): Promise<Nullable<ProjectActivityTask>> {
        LoadLayoutStore.start();

        const createdTask =
            await this.createActivityTaskUseCase.execute(activityTask);

        if (createdTask) {
            ToastManagerStore.success();
        }

        LoadLayoutStore.finish();
        return createdTask;
    }

    async onDeleteTask(taskId: number): Promise<boolean> {
        LoadLayoutStore.start();

        const isSuccessfullyDeleted =
            await this.deleteActivityTaskUseCase.delete(taskId);

        if (isSuccessfullyDeleted) {
            ToastManagerStore.success();
            await this.getAllActivities();
        }

        LoadLayoutStore.finish();
        return isSuccessfullyDeleted;
    }

    async onUpdateTask(
        activityTask: ProjectActivityTask,
    ): Promise<Nullable<ProjectActivityTask>> {
        LoadLayoutStore.start();

        const updatedTask =
            await this.updateActivityTaskUseCase.update(activityTask);

        if (updatedTask) {
            ToastManagerStore.success();
            await this.getAllActivities();
        }

        LoadLayoutStore.finish();
        return updatedTask;
    }

    @action.bound
    openTaskIndicatorUpdateProposal(
        task: ProjectActivityTask,
        indicators: Indicator[],
        indicatorProgress: IndicatorProgressSummary[],
    ): void {
        const indicatorProgressById = new globalThis.Map(
            indicatorProgress.map((progressItem) => [
                progressItem.indicatorId,
                progressItem,
            ]),
        );

        this.completedTaskToReview = task;
        this.indicatorsToReview = indicators.map((indicator) => ({
            ...this.buildTaskCompletionProposal(
                indicator,
                task,
                indicatorProgressById.get(indicator.id),
            ),
            slots: indicator.slots ? [...indicator.slots] : undefined,
            tasks: indicator.tasks ? [...indicator.tasks] : undefined,
        }));
        this.taskIndicatorUpdateProposalOpen = true;
    }

    @action.bound
    closeTaskIndicatorUpdateProposal(): void {
        this.taskIndicatorUpdateProposalOpen = false;
        this.taskIndicatorUpdateSaving = false;
        this.completedTaskToReview = null;
        this.indicatorsToReview = [];
    }

    @action.bound
    async applyTaskIndicatorUpdateProposal(
        indicators: Indicator[],
    ): Promise<void> {
        this.taskIndicatorUpdateSaving = true;

        const completedTask = this.completedTaskToReview;

        const results = await Promise.all(
            indicators.map((indicator) =>
                this.editIndicatorUseCase.execute(indicator),
            ),
        );

        runInAction(() => {
            this.taskIndicatorUpdateSaving = false;
        });

        if (results.some((result) => result === null)) return;

        if (completedTask) {
            await Promise.allSettled(
                indicators.map((indicator) =>
                    this.createIndicatorCommentUseCase.execute(
                        indicator.id,
                        new CreateIndicatorComment(
                            i18n.t(
                                "private:project.kanban.form.taskCompletionProposalSavedComment",
                                {
                                    task: completedTask.description,
                                },
                            ),
                            IndicatorCommentTypeEnum.note,
                        ),
                    ),
                ),
            );
        }

        ToastManagerStore.success();
        this.closeTaskIndicatorUpdateProposal();
        await this.getAllActivities();
    }

    private buildTaskCompletionProposal(
        indicator: Indicator,
        task: ProjectActivityTask,
        indicatorProgress?: IndicatorProgressSummary,
    ): Indicator {
        const progressSuggestion = indicatorProgress
            ? i18n.t(
                  "private:project.kanban.form.taskCompletionSuggestedStatus",
                  {
                      completed: indicatorProgress.completedCount,
                      total: indicatorProgress.totalCount,
                  },
              )
            : i18n.t(
                  "private:project.kanban.form.taskCompletionSuggestedStatusFallback",
              );

        const verificationSuggestion = indicatorProgress
            ? i18n.t(
                  "private:project.kanban.form.taskCompletionSuggestedVerificationSource",
                  {
                      task: task.description,
                      completed: indicatorProgress.completedCount,
                      total: indicatorProgress.totalCount,
                  },
              )
            : i18n.t(
                  "private:project.kanban.form.taskCompletionSuggestedVerificationSourceFallback",
                  {
                      task: task.description,
                  },
              );

        const achievedProposal = this.buildSuggestedAchievedProgress(
            indicator,
            indicatorProgress,
        );

        return {
            ...indicator,
            achievedIndicator: achievedProposal.achievedIndicator,
            achievedGenderBreakdown: achievedProposal.achievedGenderBreakdown,
            status: appendSuggestion(indicator.status, progressSuggestion),
            verificationFont: appendSuggestion(
                indicator.verificationFont,
                verificationSuggestion,
            ),
        };
    }

    private buildSuggestedAchievedProgress(
        indicator: Indicator,
        indicatorProgress?: IndicatorProgressSummary,
    ): Pick<Indicator, "achievedIndicator" | "achievedGenderBreakdown"> {
        if (!indicatorProgress || indicatorProgress.totalCount <= 0) {
            return {
                achievedIndicator: indicator.achievedIndicator,
                achievedGenderBreakdown: indicator.achievedGenderBreakdown,
            };
        }

        if (indicator.disaggregateByGender) {
            const suggestedGenderBreakdown =
                this.buildSuggestedAchievedGenderBreakdown(
                    indicator,
                    indicatorProgress,
                );

            if (!suggestedGenderBreakdown) {
                return {
                    achievedIndicator: indicator.achievedIndicator,
                    achievedGenderBreakdown: indicator.achievedGenderBreakdown,
                };
            }

            const totalTemplate =
                indicator.achievedIndicator ?? indicator.estimatedIndicator;

            return {
                achievedIndicator: formatNumericIndicatorValue(
                    totalTemplate,
                    getBreakdownNumericSum(suggestedGenderBreakdown),
                ),
                achievedGenderBreakdown: suggestedGenderBreakdown,
            };
        }

        const estimatedValue = parseNumericIndicatorValue(
            indicator.estimatedIndicator,
        );

        if (!estimatedValue) {
            return {
                achievedIndicator: indicator.achievedIndicator,
                achievedGenderBreakdown: indicator.achievedGenderBreakdown,
            };
        }

        const progressRatio =
            indicatorProgress.completedCount / indicatorProgress.totalCount;

        const proposedNumericValue = estimatedValue.value * progressRatio;
        const currentAchievedValue = parseNumericIndicatorValue(
            indicator.achievedIndicator,
        );

        const consolidatedNumericValue = currentAchievedValue
            ? Math.max(currentAchievedValue.value, proposedNumericValue)
            : proposedNumericValue;

        const template = currentAchievedValue
            ? indicator.achievedIndicator ?? indicator.estimatedIndicator
            : indicator.estimatedIndicator;

        return {
            achievedIndicator: formatNumericIndicatorValue(
                template,
                consolidatedNumericValue,
            ),
            achievedGenderBreakdown: indicator.achievedGenderBreakdown,
        };
    }

    private buildSuggestedAchievedGenderBreakdown(
        indicator: Indicator,
        indicatorProgress: IndicatorProgressSummary,
    ): IndicatorGenderBreakdown | null {
        const progressRatio =
            indicatorProgress.completedCount / indicatorProgress.totalCount;

        const suggestedBreakdown = GENDER_BREAKDOWN_KEYS.reduce(
            (accumulator, key, index) => {
                const estimatedEntry = indicator.estimatedGenderBreakdown[key];
                const estimatedValue = parseNumericIndicatorValue(
                    estimatedEntry ?? undefined,
                );

                if (!estimatedValue) {
                    accumulator[key] =
                        indicator.achievedGenderBreakdown[key] ?? undefined;
                    return accumulator;
                }

                const currentAchievedValue = parseNumericIndicatorValue(
                    indicator.achievedGenderBreakdown[key] ?? undefined,
                );

                const proposedNumericValue =
                    estimatedValue.value * progressRatio;
                const consolidatedNumericValue = currentAchievedValue
                    ? Math.max(currentAchievedValue.value, proposedNumericValue)
                    : proposedNumericValue;

                const template =
                    indicator.achievedGenderBreakdown[key] ??
                    estimatedEntry ??
                    `${consolidatedNumericValue}`;

                accumulator[key] = formatNumericIndicatorValue(
                    template,
                    consolidatedNumericValue,
                );

                return accumulator;
            },
            {} as IndicatorGenderBreakdown,
        );

        const hasNumericEstimatedBreakdown = GENDER_BREAKDOWN_KEYS.some(
            (key) =>
                parseNumericIndicatorValue(
                    indicator.estimatedGenderBreakdown[key] ?? undefined,
                ) !== null,
        );

        if (!hasNumericEstimatedBreakdown) {
            return null;
        }

        const hasNumericSuggestedBreakdown = GENDER_BREAKDOWN_KEYS.some(
            (key) =>
                parseNumericIndicatorValue(
                    suggestedBreakdown[key] ?? undefined,
                ) !== null,
        );

        return hasNumericSuggestedBreakdown ? suggestedBreakdown : null;
    }

    @action
    setAlertSettingsModalOpen(open: boolean): void {
        this.alertSettingsModalOpen = open;
    }

    @action
    setAlertSettings(settings: ActivityAlertSettings): void {
        this.alertSettings = settings;
    }

    async openAlertSettingsModal(): Promise<void> {
        this.setAlertSettingsModalOpen(true);
        await this.loadAlertSettings();
    }

    async loadAlertSettings(): Promise<void> {
        this.alertSettingsLoading = true;

        const result = await this.getMyActivityAlertSettingsUseCase.execute();

        runInAction(() => {
            this.alertSettingsLoading = false;
        });

        if (result.isLeft()) return;

        runInAction(() => {
            this.alertSettings = result.getOrThrow();
        });
    }

    async saveAlertSettings(): Promise<void> {
        this.alertSettingsSaving = true;

        const result = await this.updateMyActivityAlertSettingsUseCase.execute(
            this.alertSettings,
        );

        runInAction(() => {
            this.alertSettingsSaving = false;
        });

        if (result.isLeft()) return;

        runInAction(() => {
            this.alertSettings = result.getOrThrow();
        });

        ToastManagerStore.success();
        this.setAlertSettingsModalOpen(false);
    }
}
